// FIFA World Cup 2026 (v3 teardown).
//
// Group → R32 → R16 → QF → SF → Final → Champion, with Classic + Quantum
// picks per fixture, HIT / MISS / VOID chips, and a deep-dive drawer.

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Trophy, Globe2, CheckCircle2, XCircle, MinusCircle,
  Clock as ClockIcon, ChevronDown, ChevronUp, Loader2, Search,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import TopNav from "../components/TopNav";
import MatchAnalysisModal from "../components/MatchAnalysisModal";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Label,
  V3Tag, V3Num,
} from "../components/v3";

const STAGE_LABELS = {
  group: "Group Stage",
  r32: "Round of 32",
  r16: "Round of 16",
  qf: "Quarter-Finals · q8",
  sf: "Semi-Finals · q4",
  final: "Final · q2",
  champion: "Predicted Champion",
};

const STAGE_ORDER = ["group", "r32", "r16", "qf", "sf", "final", "champion"];

const formatKickoff = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const HitChip = ({ outcome, engine }) => {
  if (!outcome) return null;
  const cfg =
    outcome === "hit"  ? { Icon: CheckCircle2, tone: "emerald" }
    : outcome === "miss" ? { Icon: XCircle, tone: "rose" }
    : { Icon: MinusCircle, tone: "mute" };
  const { Icon, tone } = cfg;
  return (
    <V3Tag tone={tone} testid={`wc-pick-${engine}-${outcome}`}>
      <Icon className="w-2.5 h-2.5" />
      {outcome}
    </V3Tag>
  );
};

const MarketsDrawer = ({ classic, quantum }) => {
  const renderList = (engine, picks) => {
    if (!picks || picks.length === 0) {
      return (
        <div
          className="text-[11px] italic"
          style={{ color: "var(--v3-text-4)" }}
        >
          No {engine} markets yet.
        </div>
      );
    }
    return (
      <ul className="space-y-1.5" data-testid={`wc-markets-${engine}`}>
        {picks.map((m, i) => (
          <li key={`${engine}-${i}`} className="flex items-start justify-between gap-2 text-[11px]">
            <div className="flex-1 min-w-0">
              <div
                className="text-[9px] uppercase"
                style={{
                  color: "var(--v3-text-3)",
                  fontFamily: "var(--v3-font-mono)",
                  letterSpacing: "0.14em",
                }}
              >
                {m.market_type}
              </div>
              <div className="truncate" style={{ fontWeight: 500 }}>{m.prediction}</div>
            </div>
            <V3Num className="text-[12px] shrink-0">{Math.round(m.confidence)}%</V3Num>
          </li>
        ))}
      </ul>
    );
  };
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-0 mt-3"
      style={{ borderTop: "1px solid var(--v3-line)" }}
    >
      <div
        className="p-3"
        style={{ borderRight: "1px solid var(--v3-line)" }}
      >
        <V3Label>Classic top markets</V3Label>
        <div className="mt-2">{renderList("classic", classic?.top_predictions)}</div>
      </div>
      <div className="p-3">
        <V3Label>Quantum top markets</V3Label>
        <div className="mt-2">{renderList("quantum", quantum?.top_predictions)}</div>
      </div>
    </div>
  );
};

const MatchCard = ({ m, onAnalyze, analysisLoadingFid }) => {
  const [marketsOpen, setMarketsOpen] = useState(false);
  const home = m.home_team?.name || "TBD";
  const away = m.away_team?.name || "TBD";
  const isPlayed = ["FT", "AET", "PEN"].includes(m.status);
  const pickClassic = m.picks?.classic;
  const pickQuantum = m.picks?.quantum;
  const hasAnyPick = pickClassic || pickQuantum;
  const isLoadingAnalysis = analysisLoadingFid === m.fixture_id;

  return (
    <article
      className="p-4 transition-colors"
      style={{
        border: "1px solid var(--v3-line)",
        background: "var(--v3-surface)",
      }}
      data-testid={`wc-match-${m.fixture_id}`}
    >
      <div
        className="flex items-center justify-between text-[10px] uppercase mb-3"
        style={{
          color: "var(--v3-text-3)",
          fontFamily: "var(--v3-font-mono)",
          letterSpacing: "0.14em",
        }}
      >
        <span>{m.round || "—"}</span>
        <span className="inline-flex items-center gap-1">
          <ClockIcon className="w-3 h-3" strokeWidth={1.75} />
          {formatKickoff(m.kickoff)}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {m.home_team?.logo && <img src={m.home_team.logo} alt="" className="w-6 h-6" />}
          <span className="truncate" style={{ fontWeight: 500 }}>{home}</span>
        </div>
        <div className="text-center min-w-[60px]">
          {isPlayed ? (
            <V3Num className="text-2xl">{m.goals_home}-{m.goals_away}</V3Num>
          ) : (
            <span
              className="text-[10px] uppercase"
              style={{
                color: "var(--v3-text-4)",
                fontFamily: "var(--v3-font-mono)",
                letterSpacing: "0.16em",
              }}
            >
              vs
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <span className="truncate" style={{ fontWeight: 500 }}>{away}</span>
          {m.away_team?.logo && <img src={m.away_team.logo} alt="" className="w-6 h-6" />}
        </div>
      </div>

      {hasAnyPick && (
        <div
          className="mt-4 pt-3 space-y-2"
          style={{ borderTop: "1px solid var(--v3-line)" }}
        >
          {pickClassic && (
            <div className="flex items-center gap-2 text-[12px]" data-testid={`wc-match-${m.fixture_id}-classic`}>
              <V3Label className="w-16 shrink-0">Classic</V3Label>
              <span className="flex-1 truncate" style={{ fontWeight: 500 }}>{pickClassic.winner}</span>
              <V3Num tone="var(--v3-indigo-fg)" className="text-[12px]">{pickClassic.confidence}%</V3Num>
              <HitChip outcome={m.hit?.classic} engine="classic" />
            </div>
          )}
          {pickQuantum && (
            <div className="flex items-center gap-2 text-[12px]" data-testid={`wc-match-${m.fixture_id}-quantum`}>
              <V3Label className="w-16 shrink-0">Quantum</V3Label>
              <span className="flex-1 truncate" style={{ fontWeight: 500 }}>{pickQuantum.winner}</span>
              <V3Num tone="var(--v3-indigo-fg)" className="text-[12px]">{pickQuantum.confidence}%</V3Num>
              <HitChip outcome={m.hit?.quantum} engine="quantum" />
            </div>
          )}
        </div>
      )}

      <div
        className="flex items-center gap-2 mt-3 pt-3"
        style={{ borderTop: "1px solid var(--v3-line)" }}
      >
        {hasAnyPick && (
          <button
            type="button"
            onClick={() => setMarketsOpen((v) => !v)}
            data-testid={`wc-match-${m.fixture_id}-toggle-markets`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] uppercase py-1.5 transition-colors"
            style={{
              color: "var(--v3-text-3)",
              fontFamily: "var(--v3-font-mono)",
              letterSpacing: "0.14em",
              border: "1px solid var(--v3-line-2)",
            }}
          >
            {marketsOpen ? <ChevronUp className="w-3 h-3" strokeWidth={1.75} /> : <ChevronDown className="w-3 h-3" strokeWidth={1.75} />}
            {marketsOpen ? "Hide markets" : "Show markets"}
          </button>
        )}
        <button
          type="button"
          onClick={() => onAnalyze(m.fixture_id)}
          disabled={isLoadingAnalysis}
          data-testid={`wc-match-${m.fixture_id}-analyze`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] uppercase py-1.5 transition-colors disabled:opacity-60"
          style={{
            color: "var(--v3-canvas)",
            background: "var(--v3-indigo)",
            fontFamily: "var(--v3-font-mono)",
            letterSpacing: "0.14em",
            fontWeight: 500,
          }}
        >
          {isLoadingAnalysis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" strokeWidth={1.75} />}
          Deep analysis
        </button>
      </div>

      {marketsOpen && hasAnyPick && (
        <MarketsDrawer classic={pickClassic} quantum={pickQuantum} />
      )}
    </article>
  );
};

const ChampionView = ({ overview }) => {
  const proj = overview?.champion_projection;
  if (!proj || !proj.champion) {
    return (
      <div
        className="p-10 text-center"
        style={{ border: "1px dashed var(--v3-line-2)", background: "var(--v3-surface)" }}
      >
        <Trophy className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--v3-text-4)" }} strokeWidth={1.5} />
        <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
          Champion projection unavailable yet — knockouts haven't been scheduled by FIFA.
        </p>
      </div>
    );
  }
  return (
    <div
      className="p-8"
      style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
    >
      <div className="text-center" data-testid="wc-champion-card">
        <Trophy className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--v3-indigo-fg)" }} strokeWidth={1.5} />
        <V3Label>Predicted Champion</V3Label>
        <div
          className="mt-3 text-5xl md:text-6xl"
          style={{
            fontWeight: 500,
            letterSpacing: "-0.045em",
            lineHeight: 1.05,
          }}
        >
          {proj.champion}
        </div>
        <div
          className="mt-5 inline-flex items-center gap-3 text-[12px]"
          style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
        >
          <span>Compound</span>
          <V3Num tone="var(--v3-indigo-fg)">{proj.champion_confidence}%</V3Num>
          <span style={{ color: "var(--v3-text-4)" }}>·</span>
          <span>{proj.matches_to_win} matches to win</span>
        </div>
      </div>
      {proj.path?.length > 0 && (
        <div
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-0"
          style={{ borderTop: "1px solid var(--v3-line)" }}
        >
          {["r32", "r16", "qf", "sf", "final"].map((st, idx) => {
            const stageMatches = proj.path.filter((p) => p.stage === st);
            if (!stageMatches.length) return null;
            return (
              <div
                key={st}
                className="p-4"
                style={{
                  borderRight: idx < 4 ? "1px solid var(--v3-line)" : "none",
                  borderBottom: "1px solid var(--v3-line)",
                }}
              >
                <V3Label>{STAGE_LABELS[st]}</V3Label>
                <ul className="mt-3 space-y-1.5 text-[11px]">
                  {stageMatches.slice(0, 8).map((m) => (
                    <li key={m.fixture_id} className="flex items-center justify-between gap-2">
                      <span
                        className="truncate"
                        style={{ color: m.settled ? "var(--v3-indigo-fg)" : "var(--v3-text)" }}
                      >
                        {m.winner}
                      </span>
                      <V3Num tone="var(--v3-text-3)" className="text-[11px] shrink-0">
                        {m.confidence}%
                      </V3Num>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function WorldCupPage() {
  const [overview, setOverview] = useState(null);
  const [stage, setStage] = useState("group");
  const [stageData, setStageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoadingFid, setAnalysisLoadingFid] = useState(null);

  const openAnalysis = useCallback(async (fixtureId) => {
    if (!fixtureId) return;
    setAnalysisLoadingFid(fixtureId);
    try {
      const { data } = await axios.get(`${API}/predictions/${fixtureId}/analysis`, { timeout: 30000 });
      setAnalysisData(data);
      setAnalysisOpen(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Analysis not yet available — predictions are still being compiled for this fixture.");
      } else {
        toast.error("Failed to load match analysis");
      }
    } finally {
      setAnalysisLoadingFid(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/world-cup/overview`);
        setOverview(r.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (stage === "champion") return;
    let cancelled = false;
    (async () => {
      setStageData(null);
      try {
        const r = await axios.get(`${API}/world-cup/round/${stage}`);
        if (!cancelled) setStageData(r.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [stage]);

  useEffect(() => {
    if (stage === "champion" || !stageData) return;
    const pendingClassic = stageData.lazy_pending?.classic || 0;
    const pendingQuantum = stageData.lazy_pending?.quantum || 0;
    if (pendingClassic === 0 && pendingQuantum === 0) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/world-cup/round/${stage}`);
        setStageData(r.data);
      } catch {}
    }, 30000);
    return () => clearTimeout(t);
  }, [stage, stageData]);

  const pending = stageData?.lazy_pending;

  return (
    <V3Page testid="world-cup-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="world-cup-header"
          eyebrow={<V3Eyebrow icon={Globe2}>FIFA World Cup 2026</V3Eyebrow>}
          title="World Cup."
          titleAccent="Full bracket · every pick."
          subtitle={
            overview?.total_fixtures
              ? `Group stage + every knockout round, Classic and Quantum engines side-by-side. Currently tracking ${overview.total_fixtures} fixtures.`
              : "Group stage + every knockout round, Classic and Quantum engines side-by-side. Tap any match for full deep-dive analysis."
          }
        />

        {/* Stage strip */}
        <div
          className="flex items-center gap-0 overflow-x-auto -mx-1 px-1 mb-8"
          style={{ borderBottom: "1px solid var(--v3-line)" }}
          data-testid="wc-stage-tabs"
        >
          {STAGE_ORDER.map((s) => {
            const activeTab = stage === s;
            const count = s === "champion" ? null : overview?.by_stage?.[s]?.count ?? 0;
            const settled = s === "champion" ? null : overview?.by_stage?.[s]?.settled ?? 0;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStage(s)}
                data-testid={`wc-tab-${s}`}
                className="relative px-3 py-3 text-[12px] shrink-0 transition-colors inline-flex items-center gap-1.5"
                style={{
                  color: activeTab ? "var(--v3-text)" : "var(--v3-text-3)",
                  fontWeight: activeTab ? 500 : 400,
                  letterSpacing: "-0.005em",
                }}
              >
                {STAGE_LABELS[s]}
                {count != null && (
                  <span
                    className="ml-1 text-[10px]"
                    style={{
                      color: activeTab ? "var(--v3-text-3)" : "var(--v3-text-4)",
                      fontFamily: "var(--v3-font-mono)",
                    }}
                  >
                    {settled}/{count}
                  </span>
                )}
                {activeTab && (
                  <span
                    className="absolute left-0 right-0 -bottom-px h-px"
                    style={{ background: "var(--v3-text)" }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {pending && (pending.classic > 0 || pending.quantum > 0) && (
          <div
            className="px-4 py-2.5 mb-6 text-[12px] flex items-center gap-2"
            style={{
              border: "1px solid rgba(99,102,241,0.3)",
              background: "rgba(99,102,241,0.06)",
              color: "var(--v3-indigo-fg)",
              fontFamily: "var(--v3-font-mono)",
            }}
            data-testid="wc-lazy-pending"
          >
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>
              Compiling fresh picks · {pending.classic} Classic + {pending.quantum} Quantum pending. Auto-refreshes.
            </span>
          </div>
        )}

        {stage === "champion" ? (
          <ChampionView overview={overview} />
        ) : loading || !stageData ? (
          <div
            className="py-16 text-[13px]"
            style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
          >
            Loading {STAGE_LABELS[stage].toLowerCase()}…
          </div>
        ) : stageData.items?.length === 0 ? (
          <div
            className="p-10 text-center"
            style={{ border: "1px dashed var(--v3-line-2)", background: "var(--v3-surface)" }}
          >
            <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
              No fixtures yet for {STAGE_LABELS[stage]}.
              {(stage === "r32" || stage === "r16" || stage === "qf" || stage === "sf" || stage === "final") &&
                " Knockouts get scheduled by FIFA after the group stage concludes."}
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
            data-testid={`wc-stage-${stage}-grid`}
          >
            {stageData.items.map((m) => (
              <MatchCard
                key={m.fixture_id}
                m={m}
                onAnalyze={openAnalysis}
                analysisLoadingFid={analysisLoadingFid}
              />
            ))}
          </div>
        )}
      </V3Container>
      {analysisOpen && analysisData && (
        <MatchAnalysisModal
          analysis={analysisData}
          onClose={() => { setAnalysisOpen(false); setAnalysisData(null); }}
        />
      )}
    </V3Page>
  );
}
